import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import Seo from "../components/Seo";
import { confirmPasswordReset } from "../lib/api";
import { PasswordField, PASSWORD_RULE, passwordError } from "../components/PasswordField";

/** The vendor appends its own token parameter to the resetUrl we send, so every
 *  plausible name is accepted rather than guessing one. */
const TOKEN_KEYS = ["token", "t", "resetToken", "code"];

export default function AccountResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = TOKEN_KEYS.map((k) => params.get(k)).find(Boolean) || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [state, setState] = useState({ busy: false, error: null, done: false });

  const submit = async (e) => {
    e.preventDefault();
    const pwError = passwordError(form.password, form.confirm);
    if (pwError) {
      return setState({ busy: false, error: pwError, done: false });
    }
    setState({ busy: true, error: null, done: false });
    try {
      await confirmPasswordReset(token, form.password);
      setState({ busy: false, error: null, done: true });
    } catch (err) {
      setState({ busy: false, error: err.payload?.userMessage || err.message, done: false });
    }
  };

  return (
    <>
      <Seo title="Set a new password | West Branch Marina" path="/account/reset" noindex />
      <div className="mx-auto max-w-md px-5 py-28 lg:px-10">
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-navy">
          Set a new password
        </h1>

        {!token && (
          <div data-testid="reset-missing-token" className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            This link is missing its reset code. Reset links expire, so please{" "}
            <Link to="/account/forgot-password" data-testid="reset-request-again" className="font-bold underline">
              request a new one
            </Link>
            .
          </div>
        )}

        {token && !state.done && (
          <form data-testid="reset-form" onSubmit={submit} className="mt-8 space-y-4" noValidate>
            <p className="text-sm text-slate-600" data-testid="reset-password-rule">
              {PASSWORD_RULE} Your reset link works once and then expires.
            </p>
            {state.error && (
              <div data-testid="reset-error" role="alert" aria-live="assertive" className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                {state.error}
                <Link to="/account/forgot-password" data-testid="reset-error-request-again" className="ml-1 font-bold underline">
                  Request a new link
                </Link>
              </div>
            )}
            {[["password", "New password"], ["confirm", "Confirm new password"]].map(([name, label]) => (
              <PasswordField
                key={name}
                id={`reset-${name}`}
                testId={`reset-${name}`}
                label={label}
                value={form[name]}
                onChange={(v) => setForm({ ...form, [name]: v })}
              />
            ))}
            <button
              type="submit"
              data-testid="reset-submit"
              disabled={state.busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover disabled:opacity-60"
            >
              <KeyRound className="h-4 w-4" aria-hidden="true" />
              {state.busy ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}

        {state.done && (
          <div data-testid="reset-done" role="status" className="mt-8 rounded-xl border border-lake/40 bg-lake/[0.07] p-5 text-sm text-navy">
            <p className="font-bold">Your password is set.</p>
            <button
              type="button"
              data-testid="reset-signin-next"
              onClick={() => navigate("/account/login")}
              className="mt-4 inline-flex rounded-full bg-ember px-5 py-2.5 text-xs font-bold text-white hover:bg-ember-hover"
            >
              Sign in
            </button>
          </div>
        )}
      </div>
    </>
  );
}
